// Companion/ChatChip.tsx
import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useScrollEdgeFadeX } from './useScrollEdgeFade';

export interface ChatChipProps {
  /** A leading glyph, sized by the caller (Phosphor icons only). */
  icon?: ReactNode;
  /**
   * `pill` is the chip proper — the follow-ups under a message, the drawer's
   * and the sheet's presets. `plain` drops the pill for icon and label alone,
   * unchanged on hover: the desktop's suggestion columns are a list to read
   * down, where a stack of pills reads as buttons competing with the composer.
   */
  variant?: 'pill' | 'plain';
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  /** Carries the caller's entry delay; the chip owns no timing of its own. */
  style?: CSSProperties;
}

/**
 * The companion's one chip (ORA-603): a rounded, tappable suggestion.
 *
 * The empty state's presets and the follow-up chips (ORA-606) are the same
 * affordance in two places, so the shape lives here and the callers bring the
 * icon, the label and the handler. Purely presentational — a chip never knows
 * what sending means.
 *
 * The target is 36px tall everywhere, and the label drops to 12px on a phone.
 * Chips are a suggestion, not the main content: on a phone they sit on one
 * scrollable line (see `ChatChipRow`) and must not stand in for the thread or
 * the composer, which is what a 44px block of wrapped chips was doing
 * (ORA-628). They stay comfortably tappable at 36px because the row gives each
 * chip its own line of space.
 *
 * In light mode the chip sits on the page gradient, so it takes the primary
 * surface to lift off it; dark mode keeps the raised surface over the glass.
 */
const ChatChip = ({
  icon,
  children,
  onClick,
  disabled = false,
  variant = 'pill',
  className,
  style,
}: ChatChipProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={style}
    className={cn(
      'inline-flex cursor-pointer items-center gap-1.5 rounded-full text-left text-sm text-fg-primary transition-colors outline-none max-mobile:shrink-0 max-mobile:text-xs max-mobile:whitespace-nowrap',
      variant === 'pill' && 'min-h-9',
      variant === 'plain' && 'py-1',
      variant === 'pill' && [
        'border border-border-subtle bg-surface-canvas px-3 py-1.5 dark:bg-surface-raised',
        'hover:border-ring hover:bg-surface-muted',
        'disabled:hover:border-border-subtle disabled:hover:bg-surface-canvas dark:disabled:hover:bg-surface-raised',
      ],

      'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className
    )}
  >
    {icon ? (
      <span className="flex shrink-0 items-center text-fg-secondary">
        {icon}
      </span>
    ) : null}
    {children}
  </button>
);

/**
 * How a chip row behaves on a phone: one line that scrolls sideways, bleeding
 * into the surface padding so the last chip runs off the edge rather than
 * stopping short of it. `px-3` matches the padding every chat surface uses
 * below `mobile`, so `-mx-3` lands the row exactly on the surface edge.
 */
const CHIP_ROW_PHONE_CLASS =
  'max-mobile:-mx-3 max-mobile:-my-1 max-mobile:flex-row max-mobile:flex-nowrap max-mobile:overflow-x-auto max-mobile:overscroll-x-contain max-mobile:px-3 max-mobile:py-1 max-mobile:no-scrollbar';

/**
 * The right-edge fade, on only while there is more to scroll to. `#000` here
 * is a mask stop — opaque, not a colour — so no design token applies.
 */
const CHIP_ROW_FADE_CLASS =
  'max-mobile:[mask-image:linear-gradient(to_right,#000_calc(100%_-_2.5rem),transparent)]';

export type ChatChipRowProps = ComponentProps<'div'>;

/**
 * The row a group of chips sits in.
 *
 * From `mobile` up the caller decides how the chips fill it — wrapped on the
 * chat page, stacked in a drawer. Below it there is one rule for every chip
 * group: a single line that scrolls sideways and fades at its right edge while
 * there is more (ORA-628). A phone's screen belongs to the thread and the
 * composer, so the suggestions take one line of it and no more.
 */
export const ChatChipRow = ({
  className,
  children,
  ...rest
}: ChatChipRowProps) => {
  const { ref, hasMoreRight } = useScrollEdgeFadeX<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={cn(
        'flex gap-2',
        CHIP_ROW_PHONE_CLASS,
        hasMoreRight && CHIP_ROW_FADE_CLASS,
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

export default ChatChip;
